import React, { useEffect, useMemo, useState } from "react";
import { Search, Loader2, BookMarked, Sparkles, X } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  Candlestick: "#D18E63",
  "Chart pattern": "#547A8F",
  Indicator: "#5C8D73",
  "Technical analysis": "#2A2F2B",
  Options: "#9D6FB4",
  Macro: "#6B7068",
  "Risk management": "#C66B59",
  Fundamental: "#3A6B57",
  Other: "#6B7068",
};

const formatDetails = (text) =>
  (text || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

export default function GlossarySearch() {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently expanded entry
  const [defining, setDefining] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const r = await api.get(`/glossary${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(r.data.items || []);
    } catch (_) {
      toast.error("Could not load glossary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => load(query), 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const askAI = async () => {
    const term = query.trim();
    if (!term) {
      toast.error("Type a term first");
      return;
    }
    setDefining(true);
    try {
      const r = await api.post("/glossary/define", { term });
      setActive(r.data);
      // Refresh the list so the new term shows up immediately
      load(query);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate definition");
    } finally {
      setDefining(false);
    }
  };

  // Group by category for nicer browsing when no query
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const cat = it.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(it);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  const showNoMatchAI =
    !loading && query.trim().length >= 2 && items.length === 0;

  return (
    <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6" data-testid="glossary-search">
      <div className="flex items-center gap-2 mb-1.5">
        <BookMarked className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
          Glossary
        </span>
      </div>
      <h3 className="font-heading text-2xl tracking-tight text-[#2A2F2B] mb-1">
        Look up any trading term
      </h3>
      <p className="text-sm text-[#6B7068] mb-5">
        25 hand-curated definitions, plus AI-generated explanations on demand for
        anything else (powered by Claude).
      </p>

      <div className="relative mb-4">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7068]"
          strokeWidth={1.5}
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Try 'topping tail', 'head and shoulders', 'theta'..."
          data-testid="glossary-search-input"
          className="pl-10 pr-10 rounded-full border-[#EAE6E1] bg-[#F9F8F6] focus:bg-white"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            data-testid="glossary-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7068] hover:text-[#2A2F2B]"
            aria-label="Clear"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {showNoMatchAI && (
        <div
          className="rounded-2xl bg-[#FFF8F1] border border-[#D18E63]/30 p-5 mb-4"
          data-testid="glossary-ai-cta"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
            <span className="text-sm font-semibold text-[#2A2F2B]">
              Not in the glossary yet
            </span>
          </div>
          <p className="text-sm text-[#6B7068] mb-3">
            Want a beginner-friendly explanation of <strong>"{query}"</strong>?
            UpTick AI can write one in a few seconds and add it here.
          </p>
          <Button
            onClick={askAI}
            disabled={defining}
            data-testid="glossary-ai-define-btn"
            className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
          >
            {defining ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Asking UpTick AI...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Generate definition
              </>
            )}
          </Button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="text-sm text-[#6B7068] flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading glossary...
        </div>
      ) : items.length === 0 && !showNoMatchAI ? (
        <div className="text-sm text-[#6B7068] py-4">No matching terms.</div>
      ) : (
        <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2" data-testid="glossary-list">
          {grouped.map(([cat, list]) => (
            <section key={cat}>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-2">
                {cat}
              </div>
              <div className="space-y-2">
                {list.map((it) => {
                  const expanded = active && active.slug === it.slug;
                  return (
                    <div
                      key={it.slug}
                      data-testid={`glossary-item-${it.slug}`}
                      className="rounded-xl border border-[#EAE6E1] bg-[#F9F8F6] hover:bg-white transition-soft"
                    >
                      <button
                        onClick={() =>
                          setActive(expanded ? null : it)
                        }
                        data-testid={`glossary-item-toggle-${it.slug}`}
                        className="w-full text-left px-4 py-3 flex items-start gap-3"
                      >
                        <span
                          className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: CATEGORY_COLORS[cat] || "#6B7068",
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#2A2F2B]">
                              {it.term}
                            </span>
                            {it.source === "llm" && (
                              <span
                                className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#D18E63] flex items-center gap-0.5"
                                title="Generated by UpTick AI"
                              >
                                <Sparkles className="w-2.5 h-2.5" strokeWidth={2} /> AI
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#6B7068] mt-0.5 leading-snug">
                            {it.summary}
                          </p>
                        </div>
                      </button>
                      {expanded && (
                        <div
                          className="px-4 pb-4 pt-1 border-t border-[#EAE6E1] mt-1"
                          data-testid={`glossary-detail-${it.slug}`}
                        >
                          {formatDetails(it.details).map((p, i) => (
                            <p
                              key={`${it.slug}-p-${i}`}
                              className="text-sm text-[#2A2F2B] leading-relaxed mb-2 last:mb-0 whitespace-pre-line"
                            >
                              {p}
                            </p>
                          ))}
                          {it.examples?.length > 0 && (
                            <div className="mt-3 rounded-lg bg-[#FFF8F1] p-3 border border-[#D18E63]/20">
                              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#D18E63] mb-1">
                                Example
                              </div>
                              {it.examples.map((ex, i) => (
                                <p
                                  key={`${it.slug}-ex-${i}`}
                                  className="text-xs text-[#2A2F2B] leading-relaxed"
                                >
                                  {ex}
                                </p>
                              ))}
                            </div>
                          )}
                          {it.see_also?.length > 0 && (
                            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
                                See also:
                              </span>
                              {it.see_also.map((s) => (
                                <button
                                  key={s}
                                  onClick={() => setQuery(s)}
                                  data-testid={`glossary-see-also-${s}`}
                                  className="text-xs text-[#547A8F] hover:underline"
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
